import { useState, useEffect } from "react";
import { X, Save } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface AddOptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  initialValue?: string;
  onSave?: (value: string) => void;
  isLoading?: boolean;
}

export function AddOptionDialog({
  open,
  onOpenChange,
  title = "Add New Option",
  inputLabel = "Name",
  inputPlaceholder = "Enter name",
  initialValue = "",
  onSave,
  isLoading = false,
}: AddOptionDialogProps) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [initialValue, open]);

  const handleSave = () => {
    if (value.trim() && onSave) {
      onSave(value.trim());
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setValue("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="aero:sm:max-w-[500px] aero:p-6 aero:gap-0">
        <style>{`
          [data-slot="dialog-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <DialogHeader className="aero:flex aero:flex-row aero:items-center aero:justify-between aero:h-9 aero:mb-4">
          <DialogTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
            {title}
          </DialogTitle>
          <button
            type="button"
            onClick={handleClose}
            className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
          >
            <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
          </button>
        </DialogHeader>

        {/* Divider */}
        <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-400 aero:mb-4" />

        {/* Content */}
        <div className="aero:flex aero:flex-col aero:gap-4">
          <div className="aero:flex aero:flex-col aero:gap-2">
            <Label className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground">
              {inputLabel}
              <span className="aero:text-red-500 aero:ml-0.5">*</span>
            </Label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={inputPlaceholder}
              className="aero:font-inter aero:font-normal aero:text-base aero:leading-5 aero:text-foreground"
              autoFocus
            />
          </div>

          {/* Footer */}
          <div className="aero:flex aero:justify-end">
            <Button
              type="button"
              intent="primary"
              onClick={handleSave}
              disabled={!value.trim() || isLoading}
              className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:bg-brand-primary-700 aero:hover:bg-brand-primary-800 aero:font-inter aero:font-bold aero:text-sm aero:leading-5 aero:text-neutral-50"
            >
              {isLoading ? (
                <>
                  <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="aero:w-4 aero:h-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
